import { memo, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  LinearProgress,
  Button,
} from '@mui/material';
import { HospitalizationDto } from '@act/shared/data-transfer-objects';
import { useAppDispatch } from 'redux/store-hooks';
import {
  getCurrentAndPendingHospitalizations,
  hospitalizationsSliceSelectors,
} from 'redux/slices/hospitalizations.slice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { useInteractionsDialog } from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { DateTime } from 'luxon';
import { IconLibrary, TaggableChip } from '@act/storybook';
import NoDataMessage from '@act/toolkit/tables/NoDataMessage';

interface MemberActionButtonsProps {
  member: HospitalizationDto['member'];
  onCallMember: (fullMember: HospitalizationDto['member']) => void;
  onTextMember: (fullMember: HospitalizationDto['member']) => void;
}

const MemberActionButtons = ({
  member,
  onCallMember,
  onTextMember,
}: MemberActionButtonsProps) => {
  const { generateTrackingProps } = useTracking();
  // Get the full member data from memberSlice which includes phone numbers
  const fullMemberData = membersAppSelectors.useSelectMemberById(
    member?.id || ''
  );

  const handleCallClick = () => {
    // Use full member data if available, otherwise fall back to the limited member data
    const memberToUse = fullMemberData || member;
    onCallMember(memberToUse);
  };

  const handleTextClick = () => {
    const memberToUse = fullMemberData || member;
    onTextMember(memberToUse);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mt: 2,
        ml: -2,
        mr: -2,
        mb: -2,
        justifyContent: 'flex-start',
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        size="large"
        onClick={handleCallClick}
        sx={{ borderRadius: 8 }}
        {...generateTrackingProps('call-member-button')}
      >
        Call member
      </Button>
      <Button
        variant="outlined"
        size="large"
        onClick={handleTextClick}
        sx={{ borderRadius: 8 }}
        {...generateTrackingProps('text-member-button')}
      >
        Text member
      </Button>
    </Box>
  );
};

interface HospitalizationPanelProps {
  guideId: string;
}

const HospitalizationPanel = memo(({ guideId }: HospitalizationPanelProps) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigation();
  const { createCall, createMessage } = useInteractionsDialog();
  const { generateTrackingProps } = useTracking();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedHospitalization, setSelectedHospitalization] =
    useState<HospitalizationDto | null>(null);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    noAttempt: true, // Open by default
    notYetOffered: true, // Open by default
  });
  const [memberHistoryExpanded, setMemberHistoryExpanded] = useState<
    Record<string, boolean>
  >({});
  const [progressDetailsExpanded, setProgressDetailsExpanded] = useState(false);

  // Redux selectors
  const hospitalizationData =
    hospitalizationsSliceSelectors.useSelectCurrentAndPendingHospitalizationsGrouped();
  const status =
    hospitalizationsSliceSelectors.useSelectCurrentAndPendingStatus();
  const loading = status === 'loading';
  const error = status === 'failed';

  // Menu handlers
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    hospitalization: HospitalizationDto
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedHospitalization(hospitalization);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedHospitalization(null);
  };

  const handleViewHospitalizationEvent = () => {
    if (selectedHospitalization?.member) {
      navigate.toMemberPage(
        selectedHospitalization.member.id,
        selectedHospitalization.memberId,
        {
          externalType: 'hospitalization',
          externalId: selectedHospitalization.id,
        }
      );
    }
    handleMenuClose();
  };

  const handleCallMember = (member: HospitalizationDto['member']) => {
    if (member) {
      createCall({ user: member });
    }
  };

  const handleTextMember = (member: HospitalizationDto['member']) => {
    if (member) {
      createMessage({
        user: member,
        phoneNumber: member.phoneNumbers[0].number,
      });
    }
  };

  const toggleSection = (sectionKey: 'noAttempt' | 'notYetOffered') => {
    setSectionsExpanded(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleMemberHistory = (memberId: string) => {
    setMemberHistoryExpanded(prev => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  const toggleProgressDetails = () => {
    setProgressDetailsExpanded(prev => !prev);
  };

  // Fetch hospitalizations when guideId changes
  useEffect(() => {
    if (guideId && guideId !== 'none') {
      dispatch(
        getCurrentAndPendingHospitalizations({
          params: { guideIds: [guideId] },
        })
      );
    }
  }, [dispatch, guideId]);

  const formatDate = (dateString: string | null, isAssumed: boolean) => {
    if (!dateString) return 'N/A';
    const formattedDateString = DateTime.fromFormat(
      dateString,
      'yyyy-MM-dd'
    ).toFormat('LLL dd');
    if (isAssumed) {
      return formattedDateString;
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {formattedDateString}
        <IconLibrary iconName="SealCheck" />
      </Box>
    );
  };

  const formatTimelineDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return DateTime.fromFormat(dateString, 'yyyy-MM-dd').toFormat('MM/dd/yy');
  };

  // Create timeline events from hospitalizations
  const createTimelineEvents = (hospitalizations: HospitalizationDto[]) => {
    const events: Array<{
      date: string;
      type: 'admission' | 'discharge';
      hospitalization: HospitalizationDto;
    }> = [];

    hospitalizations.forEach(h => {
      if (h.admitDate) {
        events.push({
          date: h.admitDate,
          type: 'admission',
          hospitalization: h,
        });
      }
      if (h.dischargeDate) {
        events.push({
          date: h.dischargeDate,
          type: 'discharge',
          hospitalization: h,
        });
      }
    });

    // Sort by date descending (most recent first)
    return events.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  // Get the most recent hospitalization
  const getMostRecentHospitalization = (
    hospitalizations: HospitalizationDto[]
  ) => {
    return hospitalizations.reduce((latest, current) => {
      const latestDate = new Date(latest.admitDate || 0);
      const currentDate = new Date(current.admitDate || 0);
      return currentDate > latestDate ? current : latest;
    });
  };

  // Helper function to categorize members based on hospitalization status
  const categorizeMembersByStatus = (
    hospitalizationData: Map<string, HospitalizationDto[]>
  ) => {
    const noAttemptMembers = new Map<string, HospitalizationDto[]>();
    const notYetOfferedMembers = new Map<string, HospitalizationDto[]>();

    for (const [memberId, hospitalizations] of hospitalizationData) {
      // Check if member has ANY hospitalization with no attemptedAt
      const hasNoAttempt = hospitalizations.some(h => !h.attemptedAt);

      if (hasNoAttempt) {
        // Member goes to "No Attempt" section
        noAttemptMembers.set(memberId, hospitalizations);
      } else {
        // Check if member has ANY hospitalization with no offeredAt
        const hasNotOffered = hospitalizations.some(h => !h.offeredAt);

        if (hasNotOffered) {
          // Member goes to "Not yet offered" section
          notYetOfferedMembers.set(memberId, hospitalizations);
        }
        // If all hospitalizations have both attemptedAt and offeredAt,
        // member is not shown in any section
      }
    }

    return { noAttemptMembers, notYetOfferedMembers };
  };

  // Helper function to calculate progress
  const calculateProgress = (
    hospitalizationData: Map<string, HospitalizationDto[]>
  ) => {
    if (!hospitalizationData || hospitalizationData.size === 0) {
      return { currentPoints: 0, totalPoints: 0, percentage: 0 };
    }

    let currentPoints = 0;
    const totalMembers = hospitalizationData.size;
    const totalPoints = totalMembers * 2; // Each member is worth 2 points

    for (const [_memberId, hospitalizations] of hospitalizationData) {
      // Check if ALL hospitalizations for this member have offeredAt
      const allOffered = hospitalizations.every(h => h.offeredAt);

      if (allOffered) {
        // If member has offeredAt, they get full 2 points regardless of attemptedAt
        currentPoints += 2;
      } else {
        // Check if ALL hospitalizations for this member have attemptedAt
        const allAttempted = hospitalizations.every(h => h.attemptedAt);
        if (allAttempted) {
          currentPoints += 1; // 1 point for attempted only
        }
      }
    }

    const percentage =
      totalPoints > 0 ? (currentPoints / totalPoints) * 100 : 0;

    return { currentPoints, totalPoints, percentage };
  };
  // Reusable progress bar component
  const renderProgressBar = (isCompleted: boolean = false) => {
    if (!hospitalizationData || hospitalizationData.size === 0) return null;

    const displayProgress = isCompleted ? 100 : progress.percentage;
    const offeredCount = isCompleted
      ? hospitalizationData.size
      : Array.from(hospitalizationData.values()).filter(hospitalizations =>
          hospitalizations.every(h => h.offeredAt)
        ).length;

    // Calculate attempt counts (separate from progress points)
    const totalPossibleAttempts = hospitalizationData.size;
    const actualAttempts = isCompleted
      ? hospitalizationData.size
      : Array.from(hospitalizationData.values()).filter(hospitalizations =>
          hospitalizations.every(h => h.attemptedAt)
        ).length;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', mt: '16px !important', mb: 0, ml: 1 }}
        >
          Daily follow up progress
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <LinearProgress
            variant="determinate"
            value={displayProgress}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: 'grey.200',
              flex: 1,
              mr: 1,
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#26AB76',
                borderRadius: 6,
              },
            }}
          />

          <IconButton
            size="small"
            onClick={toggleProgressDetails}
            sx={{
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            {...generateTrackingProps('progress-details-toggle')}
          >
            {progressDetailsExpanded ? (
              <IconLibrary iconName="CaretUp" />
            ) : (
              <IconLibrary iconName="CaretDown" />
            )}
          </IconButton>
        </Box>

        <Collapse in={progressDetailsExpanded}>
          <Box
            sx={{
              mt: 1,
              ml: -1,
              mr: -1,
              p: 2,
              backgroundColor: 'grey.100',
              borderRadius: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {actualAttempts}/{totalPossibleAttempts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Attempts made
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {offeredCount}/{hospitalizationData.size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow ups offered
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography sx={{ mt: 1 }}>Loading hospitalizations...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">
          Failed to load hospitalization data
        </Typography>
      </Box>
    );
  }

  if (!hospitalizationData || hospitalizationData.size === 0) {
    return (
      <Box margin="16px 0 24px" flexShrink={0} height="fit-content">
        <NoDataMessage
          iconName="FirstAid"
          message="No current or pending hospitalizations"
        />
      </Box>
    );
  }

  // Categorize members by their hospitalization status
  const { noAttemptMembers, notYetOfferedMembers } =
    categorizeMembersByStatus(hospitalizationData);

  // Calculate progress
  const progress = calculateProgress(hospitalizationData);

  // Helper function to render a member section
  const renderMemberSection = (
    members: Map<string, HospitalizationDto[]>,
    sectionTitle: string,
    sectionColor: 'error' | 'warning',
    sectionKey: 'noAttempt' | 'notYetOffered'
  ) => {
    if (members.size === 0) return null;

    const isExpanded = sectionsExpanded[sectionKey];

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 2,
            ml: 1,
          }}
          onClick={() => toggleSection(sectionKey)}
          {...generateTrackingProps(`${sectionKey}-section-toggle`)}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: sectionColor === 'error' ? 'error.main' : 'text.primary',
                fontWeight: 'normal',
                whiteSpace: 'nowrap',
              }}
            >
              {sectionColor === 'error' && (
                <IconLibrary iconName="WarningCircle" />
              )}
              {sectionTitle} ({members.size})
            </Typography>

            <Box
              sx={{
                borderBottom: '1px solid',
                borderColor:
                  sectionColor === 'error' ? 'error.main' : '#000000',
                flex: 1,
                mx: 2,
              }}
            />

            <IconButton
              size="small"
              {...generateTrackingProps(`${sectionKey}-expand-button`)}
            >
              {isExpanded ? (
                <IconLibrary iconName="CaretUp" />
              ) : (
                <IconLibrary iconName="CaretDown" />
              )}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          {Array.from(members.entries()).map(([memberId, hospitalizations]) => {
            const member = hospitalizations[0]?.member;
            const primaryHospitalization = hospitalizations[0];

            if (!member || !primaryHospitalization) return null;

            // Determine the follow-up action needed
            const hasNoAttempt = hospitalizations.some(h => !h.attemptedAt);
            const followUpAction = hasNoAttempt
              ? 'Call before'
              : 'Follow up by';

            // Get the earliest follow-up date
            const followUpDate = hospitalizations
              .map(h => h.followUpDate)
              .filter(Boolean)
              .sort()[0];

            const timelineEvents = createTimelineEvents(hospitalizations);
            const mostRecentHospitalization =
              getMostRecentHospitalization(hospitalizations);
            const isHistoryExpanded = memberHistoryExpanded[memberId] || false;

            return (
              <Card key={memberId} sx={{ mb: 1, ml: 0 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Multiple hospitalizations chip */}
                  {hospitalizations.length > 1 && (
                    <Box sx={{ mb: 1 }}>
                      <TaggableChip
                        label="Multiple hospitalizations"
                        isTag
                        color="warning"
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {member.firstName} {member.lastName}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {followUpAction} |{' '}
                        {followUpDate
                          ? DateTime.fromFormat(
                              followUpDate,
                              'yyyy-MM-dd'
                            ).toFormat('LLL dd')
                          : 'TBD'}
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={e => handleMenuClick(e, primaryHospitalization)}
                      size="small"
                      {...generateTrackingProps('member-menu-button')}
                    >
                      <IconLibrary
                        iconName="DotsThreeVertical"
                        weight="regular"
                      />
                    </IconButton>
                  </Box>

                  {/* Hospitalization History Section */}
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        py: 1,
                      }}
                      onClick={() => toggleMemberHistory(memberId)}
                      {...generateTrackingProps('member-history-toggle')}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        Hospitalization history
                      </Typography>
                      <IconButton
                        size="small"
                        {...generateTrackingProps(
                          'member-history-expand-button'
                        )}
                      >
                        {isHistoryExpanded ? (
                          <IconLibrary iconName="CaretUp" />
                        ) : (
                          <IconLibrary iconName="CaretDown" />
                        )}
                      </IconButton>
                    </Box>

                    <Collapse in={isHistoryExpanded}>
                      {/* Timeline - Grey box extending to card edges */}
                      <Box
                        sx={{
                          mt: 1,
                          mx: -2, // Negative margin to extend to card edges
                          p: 2,
                          backgroundColor: 'grey.50',
                        }}
                      >
                        {timelineEvents.map((event, index) => (
                          <Box
                            key={`${event.date}-${event.type}-${index}`}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              py: 0.5,
                            }}
                          >
                            <Typography variant="body2">
                              {formatTimelineDate(event.date)}
                            </Typography>
                            <Typography variant="body2">
                              {event.type === 'admission'
                                ? 'Hospital Admission'
                                : 'Hospital Discharge'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Most Recent Hospitalization Details - White background */}
                      <Box sx={{ mt: 2, p: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', mb: 1 }}
                        >
                          Hospital
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {mostRecentHospitalization.hospital}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', mb: 1 }}
                        >
                          Discharge Date
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {formatDate(
                            mostRecentHospitalization.dischargeDate,
                            mostRecentHospitalization.isAssumed
                          )}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', mb: 1 }}
                        >
                          Admitting Diagnosis
                        </Typography>
                        <Typography variant="body2">
                          {mostRecentHospitalization.admitComplaint || 'N/A'}
                        </Typography>

                        {/* Call and Text Member Buttons */}
                        <MemberActionButtons
                          member={member}
                          onCallMember={handleCallMember}
                          onTextMember={handleTextMember}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Collapse>
      </Box>
    );
  };

  // Show completed view if no members need attention
  if (noAttemptMembers.size === 0 && notYetOfferedMembers.size === 0) {
    return (
      <TrackingProvider contextName="hospitalization-panel">
        <Box sx={{ p: 1 }}>
          {/* Progress Bar - show completed state */}
          {renderProgressBar(true)}

          {/* Celebration Section */}
          <Box sx={{ textAlign: 'center', py: 4, mb: 8, mt: 8 }}>
            <Typography variant="h1" sx={{ fontSize: '4rem' }}>
              🎉
            </Typography>
          </Box>

          {/* Success Message in Grey Box */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'grey.100',
              borderRadius: 6,
              textAlign: 'center',
              ml: 1,
              mr: 1,
              mt: 2,
              mb: 2,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              No follow ups are due. Great work!
            </Typography>
          </Box>
        </Box>
      </TrackingProvider>
    );
  }

  return (
    <TrackingProvider contextName="hospitalization-panel">
      <Box sx={{ p: 1 }}>
        {/* Progress Bar - only show when there's data */}
        {renderProgressBar()}

        {renderMemberSection(
          noAttemptMembers,
          'No attempts',
          'error',
          'noAttempt'
        )}
        {renderMemberSection(
          notYetOfferedMembers,
          'Not offered',
          'warning',
          'notYetOffered'
        )}

        {/* Kebab Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={handleViewHospitalizationEvent}
            {...generateTrackingProps('view-hospitalization-event-menu-item')}
          >
            View hospitalization event
          </MenuItem>
        </Menu>
      </Box>
    </TrackingProvider>
  );
});

HospitalizationPanel.displayName = 'HospitalizationPanel';

export default HospitalizationPanel;
